import React from "react";

import { Insured, Policy, Premium } from "../../api/policy";
import { formatCurrency } from "../../lib/currencyUtil";
import { isNotUndefined } from "../../typeguards/isNotUndefined";
import { PolicyCoverageData, PolicyDetailsHeader, PolicyInsureds, PolicyInsuredsData } from "./PolicyDetails.state";

const DEFAULT_FALLBACK_TEXT = "";
const NEW_LINE = "\n";

const EDITABLE_POLICIES = ["DUP", "DLP", "UVATP"];

export const canEditPolicy = (policy: Policy) => EDITABLE_POLICIES.includes(policy.productCode.toUpperCase());

const isSelectedPremium = (premium: Premium) => premium.actualPeriodIndicator;

const getPremiumForInsured = (insured: Insured, premiums?: Premium[]) => {
  if (!premiums) {
    return "UNKNOWN";
  }

  const paymentPeriods = premiums
    .filter(isSelectedPremium)
    .map((p) => p.paymentPeriod)
    .filter(Boolean);

  if (paymentPeriods?.length !== 1) {
    return "UNKNOWN";
  }

  const paymentPeriod = paymentPeriods[0];

  return (
    insured.cumulatives.cumulative
      .map((c) => {
        const lowerCasePaymentPeriod = paymentPeriod.toLowerCase();
        switch (lowerCasePaymentPeriod) {
          case "maand":
            return c.totalMonthlyPremium;
          case "kwartaal":
            return c.totalQuarterlyPremium;
          case "halfjaar":
            return c.totalHalfYearlyPremium;
          case "jaar":
            return c.totalAnnualPremium;
          default:
            return undefined;
        }
      })
      .map((c) => {
        if (typeof c === "number") {
          return formatCurrency(c);
        }
        return DEFAULT_FALLBACK_TEXT;
      })
      .filter(Boolean)
      .join(NEW_LINE) || DEFAULT_FALLBACK_TEXT
  );
};

const getProductName = (productName: string) => {
  switch (productName) {
    case "DELA Uitvaartverzekering ATP":
      return "DELA Uitvaartverzekering";
    case "DELA CoöperatieSpaarPlan":
    case "DELA Cooperatie spaarPlan":
      return "DELA CoöperatiespaarPlan";
    default:
      return productName;
  }
};

const isServicesCoverCode = (coverCode: string) => {
  const coverCodeLowerCase = coverCode.toLowerCase();
  return coverCodeLowerCase === "dnstvol" || coverCodeLowerCase === "dnstkind";
};

const translateCoverCode = (coverCode: string, dictionary: Record<string, string>) => {
  const lowerCaseCoverCode = coverCode.toLowerCase();
  if (dictionary[`coverCode.${lowerCaseCoverCode}`]) {
    return dictionary[`coverCode.${lowerCaseCoverCode}`];
  }

  return coverCode;
};

const shouldShowIsPolicyProfitSharing = (policy: Policy) =>
  policy.productCode === "DUP" || policy.productCode === "UVATP";

const shouldShowPledge = (policy: Policy) => policy.productCode === "DLP";

const isChildCoverCode = (coverCode: string) => ["dnstkind", "geldkind", "atpkind"].includes(coverCode.toLowerCase());

export const getPolicyInsureds = (policy: Policy, dictionary: Record<string, string>): PolicyInsureds[] =>
  policy.insureds?.insured
    .map((insured, i) => {
      const { naturalPersonSummary, cumulatives } = insured;

      const insuranceType = cumulatives.cumulative.map(({ coverCode }) => (
        <span key={`${coverCode}${i}`}>{translateCoverCode(coverCode, dictionary)}</span>
      ));

      const insuredAmount = cumulatives.cumulative
        .map((c) => {
          const shouldHideInsuredAmount = isServicesCoverCode(c.coverCode);
          if (shouldHideInsuredAmount) {
            return DEFAULT_FALLBACK_TEXT;
          }
          return formatCurrency(c.totalInsuredCapital);
        })
        .join(NEW_LINE);

      const coverForInsured =
        policy.covers?.cover.filter(
          (c) =>
            c.validity !== "Beeindigd" &&
            c.insuredsOfCover.insuredOfCover.some((ioc) => ioc.contactId === insured.contactId)
        ) ?? [];

      if (coverForInsured.length === 0) {
        return undefined;
      }

      const premiumPaymentTerm =
        coverForInsured
          .map((c) => {
            if (c.premiumPaymentTerm) {
              return c.premiumPaymentTerm;
            }
            const cumulative = cumulatives.cumulative.find(({ coverName }) => coverName === c.name);
            if (cumulative && isChildCoverCode(cumulative.coverCode)) {
              return "Tot 25";
            }
            return DEFAULT_FALLBACK_TEXT;
          })
          .join(NEW_LINE) || DEFAULT_FALLBACK_TEXT;

      return {
        insured: [naturalPersonSummary.initials, naturalPersonSummary.prefixName, naturalPersonSummary.surName]
          .filter(Boolean)
          .join(" ")
          .concat(`${NEW_LINE}${naturalPersonSummary.dateOfBirth}`),
        insuranceType,
        insuredAmount,
        premium: getPremiumForInsured(insured, policy.premiums?.premium),
        premiumPaymentTerm,
      };
    })
    .filter(isNotUndefined) ?? [];

export const getPolicyDetailsHeader = (policy: Policy, dictionary: Record<string, string>): PolicyDetailsHeader => ({
  policyNumber: policy.policyNumber,
  productName: getProductName(policy.productName),
  title: dictionary[`insuranceType.${policy.productCode}`] ?? policy.productCode,
  insuranceTakers: policy.policyHolders?.policyHolder.map(
    ({ naturalPersonSummary: { prefixName, initials, surName } }) =>
      [initials, prefixName, surName].filter(Boolean).join(" ")
  ),
});

export const getPolicyDetails = (policy: Policy): { key: string; value: string }[] => {
  return [
    {
      key: "Ingangsdatum",
      value: policy.effectiveDate,
    },
    shouldShowIsPolicyProfitSharing(policy)
      ? {
          key: "Is de verzekerde dekking winstdelend?",
          value: "Ja",
        }
      : undefined,
    shouldShowPledge(policy)
      ? {
          key: "Verpanding",
          value: "Kijk voor de actuele status op het meest recente polisblad.",
        }
      : undefined,
    {
      key: "IBAN rekeningnummer",
      value: policy.collections?.collection.map((c) => c.areaSEPA.iban).join(NEW_LINE) || DEFAULT_FALLBACK_TEXT,
    },
    {
      key: "Betaaltermijn",
      value: getPaymentTerm(policy),
    },
    policy.partialGuardians && policy.partialGuardians.partialGuardian.length > 0
      ? {
          key: "Bewindvoering",
          value: "Ja",
        }
      : undefined,
  ].filter(isNotUndefined);
};

const translatePaymentPeriod = (paymentPeriod: string) => {
  const lowerCasePaymentPeriod = paymentPeriod.toLowerCase();

  if (lowerCasePaymentPeriod === "koopsom") {
    return "Eenmalig";
  }

  return paymentPeriod;
};

export const getPaymentTerm = (policy: Policy) =>
  policy.premiums?.premium
    .filter(isSelectedPremium)
    .map((p) => translatePaymentPeriod(p.paymentPeriod))
    .join(NEW_LINE) ?? DEFAULT_FALLBACK_TEXT;

export const getTotalPremium = (policy: Policy) =>
  policy.premiums?.premium
    .filter(isSelectedPremium)
    .map((p) => formatCurrency(p.premium))
    .join(NEW_LINE) || DEFAULT_FALLBACK_TEXT;

export const getPolicyInsuredsData = (policy: Policy, dictionary: Record<string, string>): PolicyInsuredsData => ({
  insureds: getPolicyInsureds(policy, dictionary),
  totalPremium: getTotalPremium(policy),
});

const isSmoking = (coverSmoker: string) => {
  const coverSmokerLowerCase = coverSmoker.toLowerCase();

  switch (coverSmokerLowerCase) {
    case "y":
    case "j":
      return "Ja";
    case "n":
      return "Nee";
    default:
      return coverSmoker;
  }
};

const getPolicyCoverage = (policy: Policy) =>
  policy.covers?.cover
    .filter((c) => c.validity === "Geldig")
    .map((c) => {
      const insureds = c.insuredsOfCover.insuredOfCover
        .map((insuredOfCover) => {
          const insured = policy.insureds?.insured.find((i) => i.contactId === insuredOfCover.contactId);

          if (!insured) {
            return undefined;
          }

          return {
            ...insured,
            ...insuredOfCover,
          };
        })
        .filter(isNotUndefined);

      const insuredsData = insureds
        .map(({ naturalPersonSummary, coverSmoker }) =>
          [naturalPersonSummary.initials, naturalPersonSummary.prefixName, naturalPersonSummary.surName]
            .filter(Boolean)
            .join(" ")
            .concat(` (${naturalPersonSummary.dateOfBirth})`)
            .concat(coverSmoker ? ` - Roken: ${isSmoking(coverSmoker)}` : "")
        )
        .join(NEW_LINE);

      return {
        coverageData: [
          { key: getInsuredsLabels(insureds.length), value: insuredsData },
          { key: "Verzekeringsvorm", value: c.name },
          c.reductionRate
            ? {
                key: "Annuïteitenpercentage",
                value: `${c.reductionRate} %`,
              }
            : undefined,
          {
            key: "Verzekerd bedrag",
            value: c.insuredCapital ? formatCurrency(c.insuredCapital) : DEFAULT_FALLBACK_TEXT,
          },
          { key: "Einddatum dekking", value: c.expiredOnDate ?? DEFAULT_FALLBACK_TEXT },
          {
            key: "Einddatum premiebetaling",
            value: c.premiumPaymentExpiredOnDate ?? DEFAULT_FALLBACK_TEXT,
          },
          {
            key: "Premie",
            value: `${
              c.premiums.premium
                .filter(isSelectedPremium)
                .map((p) => formatCurrency(p.premium))
                .join(NEW_LINE) ?? DEFAULT_FALLBACK_TEXT
            } per ${getPaymentTerm(policy).toLowerCase()} excl. kosten`,
          },
        ].filter(isNotUndefined),
      };
    }) ?? [];

export const getPolicyCoverageData = (policy: Policy): PolicyCoverageData | null => ({
  paymentTerm: `Totale premie per ${getPaymentTerm(policy).toLowerCase()}`,
  totalPremium: `${getTotalPremium(policy)} incl. kosten`,
  covers: getPolicyCoverage(policy),
});

export const getInsuredsLabels = (nrOfInsureds: number) => {
  if (nrOfInsureds > 1) {
    return "Verzekerden";
  }

  return "Verzekerde";
};

export const getInsuranceTakerLabels = (nrOfInsuranceTakers: number) => {
  if (nrOfInsuranceTakers > 1) {
    return "Verzekeringnemers";
  }

  return "Verzekeringnemer";
};
